import React from 'react';
import { Link } from 'react-router-dom';
import { Logo } from '../components/ui/Logo';

const styles = {
  page: {
    minHeight: '100vh',
    backgroundColor: '#ffffff',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    color: '#1F2937',
    overflowX: 'hidden' as const,
  },
  nav: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '20px 40px',
    maxWidth: '1280px',
    margin: '0 auto',
  },
  logo: {
    height: '36px',
  },
  navLinks: {
    display: 'flex',
    alignItems: 'center',
    gap: '32px',
  },
  navLink: {
    fontSize: '15px',
    fontWeight: 500,
    color: '#6B7280',
    textDecoration: 'none',
    letterSpacing: '-0.01em',
    transition: 'color 0.2s',
  },
  navCta: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#ffffff',
    backgroundColor: '#4ADE80',
    padding: '10px 20px',
    borderRadius: '8px',
    textDecoration: 'none',
    transition: 'background-color 0.2s, transform 0.1s',
    border: 'none',
    cursor: 'pointer',
  },
  hero: {
    textAlign: 'center' as const,
    padding: '80px 40px 60px',
    maxWidth: '1280px',
    margin: '0 auto',
    position: 'relative' as const,
  },
  heroHeadline: {
    fontSize: 'clamp(2.5rem, 6vw, 4.5rem)',
    fontWeight: 700,
    letterSpacing: '-0.03em',
    lineHeight: 1.08,
    marginBottom: '24px',
    color: '#1F2937',
  },
  heroAccent: {
    color: '#4ADE80',
  },
  heroSubtext: {
    fontSize: '20px',
    lineHeight: 1.6,
    color: '#6B7280',
    maxWidth: '560px',
    margin: '0 auto 40px',
    fontWeight: 400,
  },
  heroCta: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '17px',
    fontWeight: 600,
    color: '#ffffff',
    backgroundColor: '#4ADE80',
    padding: '16px 36px',
    borderRadius: '12px',
    textDecoration: 'none',
    border: 'none',
    cursor: 'pointer',
    transition: 'background-color 0.2s, transform 0.15s, box-shadow 0.2s',
    boxShadow: '0 4px 14px rgba(74, 222, 128, 0.35)',
  },
  heroMockup: {
    marginTop: '72px',
    padding: '0 20px',
    maxWidth: '1040px',
    marginLeft: 'auto',
    marginRight: 'auto',
  },
  mockupContainer: {
    borderRadius: '16px',
    overflow: 'hidden',
    boxShadow: '0 25px 60px rgba(0, 0, 0, 0.12), 0 8px 20px rgba(0, 0, 0, 0.06)',
    border: '1px solid #e5e7eb',
    backgroundColor: '#f8faf8',
    position: 'relative' as const,
  },
  mockupBar: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '12px 16px',
    backgroundColor: '#f3f4f6',
    borderBottom: '1px solid #e5e7eb',
  },
  mockupDot: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    backgroundColor: '#d1d5db',
  },
  mockupContent: {
    padding: '40px',
    minHeight: '400px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative' as const,
    background: 'linear-gradient(135deg, #f0fdf4 0%, #ffffff 50%, #f0fdf4 100%)',
  },
  features: {
    padding: '100px 40px',
    maxWidth: '1080px',
    margin: '0 auto',
  },
  featuresGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '48px',
  },
  featureItem: {
    textAlign: 'center' as const,
  },
  featureIcon: {
    width: '56px',
    height: '56px',
    borderRadius: '14px',
    backgroundColor: '#f0fdf4',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 20px',
  },
  featureTitle: {
    fontSize: '18px',
    fontWeight: 600,
    marginBottom: '10px',
    color: '#1F2937',
    letterSpacing: '-0.01em',
  },
  featureDesc: {
    fontSize: '15px',
    lineHeight: 1.6,
    color: '#6B7280',
  },
  footer: {
    borderTop: '1px solid #f3f4f6',
    padding: '40px',
    textAlign: 'center' as const,
  },
  footerInner: {
    maxWidth: '1280px',
    margin: '0 auto',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  footerText: {
    fontSize: '14px',
    color: '#9CA3AF',
  },
  footerLinks: {
    display: 'flex',
    gap: '24px',
  },
  footerLink: {
    fontSize: '14px',
    color: '#9CA3AF',
    textDecoration: 'none',
    transition: 'color 0.2s',
  },
};

// Canvas mockup SVG representing the Cascady interface
function CanvasMockup() {
  return (
    <svg width="100%" height="320" viewBox="0 0 800 320" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Node 1 - Prompt */}
      <rect x="80" y="100" width="160" height="80" rx="12" fill="#ffffff" stroke="#e5e7eb" strokeWidth="1.5"/>
      <rect x="80" y="100" width="160" height="28" rx="12" fill="#f0fdf4"/>
      <text x="110" y="119" fontSize="11" fontWeight="600" fill="#1F2937">Prompt</text>
      <text x="100" y="152" fontSize="10" fill="#6B7280">A serene mountain</text>
      <text x="100" y="166" fontSize="10" fill="#6B7280">landscape at sunset...</text>

      {/* Node 2 - Reference Image */}
      <rect x="80" y="220" width="160" height="80" rx="12" fill="#ffffff" stroke="#e5e7eb" strokeWidth="1.5"/>
      <rect x="80" y="220" width="160" height="28" rx="12" fill="#f0fdf4"/>
      <text x="110" y="239" fontSize="11" fontWeight="600" fill="#1F2937">Reference Image</text>
      <rect x="100" y="258" width="50" height="32" rx="4" fill="#e5e7eb"/>
      <rect x="158" y="258" width="50" height="32" rx="4" fill="#e5e7eb"/>

      {/* Node 3 - Generate */}
      <rect x="420" y="140" width="180" height="100" rx="12" fill="#ffffff" stroke="#4ADE80" strokeWidth="2"/>
      <rect x="420" y="140" width="180" height="28" rx="12" fill="#f0fdf4"/>
      <text x="470" y="159" fontSize="11" fontWeight="600" fill="#1F2937">Generate</text>
      <rect x="440" y="180" width="140" height="44" rx="6" fill="#f0fdf4" stroke="#e5e7eb" strokeWidth="1"/>
      <text x="476" y="206" fontSize="10" fill="#4ADE80" fontWeight="500">✨ Ready</text>

      {/* Node 4 - Result */}
      <rect x="680" y="120" width="100" height="100" rx="12" fill="#ffffff" stroke="#e5e7eb" strokeWidth="1.5"/>
      <rect x="692" y="132" width="76" height="76" rx="6" fill="url(#resultGrad)"/>

      {/* Connection lines */}
      <path d="M240 140 C 330 140, 350 170, 420 170" stroke="#4ADE80" strokeWidth="2" fill="none" strokeLinecap="round"/>
      <path d="M240 260 C 330 260, 350 210, 420 210" stroke="#4ADE80" strokeWidth="2" fill="none" strokeLinecap="round"/>
      <path d="M600 190 C 640 190, 650 170, 680 170" stroke="#4ADE80" strokeWidth="2" fill="none" strokeLinecap="round"/>

      {/* Connection dots */}
      <circle cx="240" cy="140" r="4" fill="#4ADE80"/>
      <circle cx="240" cy="260" r="4" fill="#4ADE80"/>
      <circle cx="420" cy="170" r="4" fill="#4ADE80"/>
      <circle cx="420" cy="210" r="4" fill="#4ADE80"/>
      <circle cx="600" cy="190" r="4" fill="#4ADE80"/>
      <circle cx="680" cy="170" r="4" fill="#4ADE80"/>

      {/* Grid dots in background */}
      {Array.from({length: 20}).map((_, i) =>
        Array.from({length: 8}).map((_, j) => (
          <circle key={`${i}-${j}`} cx={40 * i + 20} cy={40 * j + 20} r="1" fill="#e5e7eb" opacity="0.5"/>
        ))
      )}

      <defs>
        <linearGradient id="resultGrad" x1="692" y1="132" x2="768" y2="208">
          <stop offset="0%" stopColor="#86EFAC"/>
          <stop offset="100%" stopColor="#4ADE80"/>
        </linearGradient>
      </defs>
    </svg>
  );
}

export function Homepage1() {
  return (
    <div style={styles.page}>
      {/* Navigation */}
      <nav style={styles.nav}>
        <Link to="/">
          <Logo size="md" />
        </Link>
        <div style={styles.navLinks} className="homepage1-nav-links">
          <a href="#features" style={styles.navLink}>Features</a>
          <Link to="/pricing" style={styles.navLink}>Pricing</Link>
          <Link to="/auth" style={styles.navCta}>Get Started</Link>
        </div>
      </nav>

      {/* Hero Section */}
      <section style={styles.hero}>
        <h1 style={styles.heroHeadline}>
          Create Stunning Images<br/>
          with <span style={styles.heroAccent}>Visual AI Workflows</span>
        </h1>
        <p style={styles.heroSubtext}>
          Drag, connect, and generate. Build powerful image generation
          workflows on an intuitive visual canvas powered by AI.
        </p>
        <Link to="/auth" style={styles.heroCta}>
          Try Cascady Free
          <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
          </svg>
        </Link>

        {/* Product Mockup */}
        <div style={styles.heroMockup}>
          <div style={styles.mockupContainer}>
            <div style={styles.mockupBar}>
              <div style={styles.mockupDot}></div>
              <div style={styles.mockupDot}></div>
              <div style={styles.mockupDot}></div>
            </div>
            <div style={styles.mockupContent}>
              <CanvasMockup />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" style={styles.features}>
        <div style={styles.featuresGrid} className="homepage1-features-grid">
          {/* Feature 1 - Visual Canvas */}
          <div style={styles.featureItem}>
            <div style={styles.featureIcon}>
              <svg width="24" height="24" fill="none" stroke="#4ADE80" viewBox="0 0 24 24" strokeWidth="2">
                <rect x="3" y="3" width="7" height="7" rx="1"/>
                <rect x="14" y="3" width="7" height="7" rx="1"/>
                <rect x="3" y="14" width="7" height="7" rx="1"/>
                <path d="M14 14h7v7h-7z" rx="1"/>
                <path d="M10 6.5h4M6.5 10v4M17.5 10v4" strokeLinecap="round"/>
              </svg>
            </div>
            <h3 style={styles.featureTitle}>Visual Canvas</h3>
            <p style={styles.featureDesc}>
              Drag and drop nodes to build image generation workflows on an infinite canvas.
            </p>
          </div>

          {/* Feature 2 - AI-Powered */}
          <div style={styles.featureItem}>
            <div style={styles.featureIcon}>
              <svg width="24" height="24" fill="none" stroke="#4ADE80" viewBox="0 0 24 24" strokeWidth="2">
                <path d="M12 2L2 7l10 5 10-5-10-5z"/>
                <path d="M2 17l10 5 10-5"/>
                <path d="M2 12l10 5 10-5"/>
              </svg>
            </div>
            <h3 style={styles.featureTitle}>AI-Powered</h3>
            <p style={styles.featureDesc}>
              Google Gemini generates stunning, high-quality images from your visual workflows.
            </p>
          </div>

          {/* Feature 3 - Reference Images */}
          <div style={styles.featureItem}>
            <div style={styles.featureIcon}>
              <svg width="24" height="24" fill="none" stroke="#4ADE80" viewBox="0 0 24 24" strokeWidth="2">
                <rect x="2" y="2" width="20" height="20" rx="2"/>
                <path d="M7 2v20M17 2v20M2 12h20M2 7h5M2 17h5M17 7h5M17 17h5"/>
              </svg>
            </div>
            <h3 style={styles.featureTitle}>Reference Images</h3>
            <p style={styles.featureDesc}>
              Use up to 14 reference images for precise control over your AI-generated results.
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer style={styles.footer}>
        <div style={styles.footerInner} className="homepage1-footer-inner">
          <span style={styles.footerText}>© 2026 Cascady. All rights reserved.</span>
          <div style={styles.footerLinks} className="homepage1-footer-links">
            <Link to="/privacy" style={styles.footerLink}>Privacy</Link>
            <Link to="/terms" style={styles.footerLink}>Terms</Link>
            <Link to="/contact" style={styles.footerLink}>Contact</Link>
          </div>
        </div>
      </footer>

      {/* Responsive styles */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');

        .homepage1-nav-links a:hover {
          color: #1F2937 !important;
        }
        .homepage1-nav-links a:last-child:hover {
          background-color: #22C55E !important;
          color: #ffffff !important;
        }

        @media (max-width: 768px) {
          .homepage1-nav-links {
            gap: 16px !important;
          }
          .homepage1-nav-links a:first-child,
          .homepage1-nav-links a:nth-child(2) {
            display: none;
          }
          .homepage1-features-grid {
            grid-template-columns: 1fr !important;
            gap: 40px !important;
          }
          .homepage1-footer-inner {
            flex-direction: column !important;
            gap: 16px !important;
          }
        }

        @media (max-width: 480px) {
          nav {
            padding: 16px 20px !important;
          }
          section {
            padding-left: 20px !important;
            padding-right: 20px !important;
          }
        }
      `}</style>
    </div>
  );
}
